import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  CSSProperties,
} from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { useCustomComponent } from '@/hooks';
import { useWidgetStore } from '@/hooks/stores';
import { Country } from '../country-codes';

export interface FilterableCountryDropdownProps {
  countries: Country[];
  value: string; // selected country code
  disabled?: boolean;
  onChange: (country: Country) => void;
  className?: string;
  'aria-label'?: string;
}

const FilterableCountryDropdownComponent: React.FC<
  FilterableCountryDropdownProps
> = ({ countries, value, disabled = false, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  // Measured actual panel height; 0 means not yet measured (fall back to estimate)
  const actualPanelHeightRef = useRef<number>(0);

  const instanceId = useWidgetStore(state => state.instanceId);

  const selectedCountry = countries.find(c => c.code === value) ?? countries[0];

  // Filter by name or dial code
  const filtered = search.trim()
    ? countries.filter(c => {
        const q = search.trim().toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.dialCode.replace('+', '').startsWith(q.replace('+', ''))
        );
      })
    : countries;

  // Create portal container once, appended to document.body to escape any
  // CSS transform containing block. Copy --peptalk-* CSS vars from the widget
  // root element so the panel retains all widget theming.
  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'peptalk-country-dropdown-portal';

    // Copy --peptalk-* CSS custom properties from widget root so the panel
    // has access to all widget theme variables despite being outside the
    // scoped DOM tree.
    const widgetRoot = document.querySelector(
      `.peptalk-widget-${instanceId}`
    ) as HTMLElement | null;
    if (widgetRoot) {
      const computedStyle = getComputedStyle(widgetRoot);
      for (let i = 0; i < computedStyle.length; i++) {
        const prop = computedStyle[i];
        if (prop.startsWith('--peptalk-')) {
          el.style.setProperty(prop, computedStyle.getPropertyValue(prop));
        }
      }
    }

    document.body.appendChild(el);
    portalContainerRef.current = el;

    return () => {
      document.body.removeChild(el);
      portalContainerRef.current = null;
    };
  }, [instanceId]);

  // Compute panel position from trigger's viewport rect.
  // getBoundingClientRect() is transform-aware — it returns actual viewport
  // coordinates regardless of CSS transforms on ancestor elements.
  // Since the panel renders via portal into document.body, position:fixed now
  // correctly positions relative to the viewport (no transform ancestor).
  // panelHeight: measured actual height (preferred) or a conservative estimate.
  // The estimate is intentionally kept small so the flip only triggers when
  // the panel truly won't fit below the trigger.
  const computePanelStyle = useCallback(
    (panelHeight?: number): CSSProperties => {
      if (!triggerRef.current) return {};
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = Math.max(rect.width, 160);

      // Use visualViewport when available — accounts for pinch-zoom / virtual
      // keyboard offsets on mobile for correct fixed positioning.
      const vp = window.visualViewport;
      const viewportHeight = vp ? vp.height : window.innerHeight;
      const vpOffsetY = vp ? vp.offsetTop : 0;
      const vpOffsetX = vp ? vp.offsetLeft : 0;

      // Prefer measured height; fall back to a conservative estimate.
      // Using a smaller fallback (200px vs old 280px) avoids premature flipping
      // and positions the panel closer to the trigger on the first render.
      const height = panelHeight ?? (actualPanelHeightRef.current || 200);

      let top = rect.bottom + 4 + vpOffsetY;
      const left = rect.left + vpOffsetX;

      if (top + height > viewportHeight + vpOffsetY) {
        top = rect.top - height - 4 + vpOffsetY;
      }

      return {
        position: 'fixed',
        top,
        left,
        width: panelWidth,
        zIndex: 'var(--peptalk-z-index-modal)' as unknown as number,
      };
    },
    []
  );

  const openDropdown = () => {
    if (disabled) return;
    setPanelStyle(computePanelStyle());
    setIsOpen(true);
    setSearch('');
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const handleSelect = (country: Country) => {
    onChange(country);
    closeDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeDropdown();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const firstItem = listRef.current?.querySelector('li[role="option"]');
      if (firstItem) (firstItem as HTMLElement).focus();
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  const handleListKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    country: Country
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(country);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.nextSibling as HTMLElement | null;
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousSibling as HTMLElement | null;
      if (prev) {
        prev.focus();
      } else {
        searchRef.current?.focus();
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  // After the panel renders into the portal, measure its actual height and
  // recompute position. useLayoutEffect fires before the browser paints so
  // there is no visible jump — the user always sees the correctly-placed panel.
  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const actualHeight = panelRef.current.getBoundingClientRect().height;
    if (actualHeight > 0) {
      actualPanelHeightRef.current = actualHeight;
      setPanelStyle(computePanelStyle(actualHeight));
    }
  }, [isOpen, computePanelStyle]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const id = setTimeout(() => searchRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isOpen]);

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    const update = () => setPanelStyle(computePanelStyle());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [isOpen, computePanelStyle]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const panel = document.querySelector('.peptalk-country-dropdown__panel');
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        panel &&
        !panel.contains(target)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  const panel = (
    <div
      ref={panelRef}
      className="peptalk-country-dropdown__panel"
      style={panelStyle}
      role="dialog"
      aria-label="Select country"
    >
      {/* Search input */}
      <div className="peptalk-country-dropdown__search-wrapper">
        <input
          ref={searchRef}
          type="text"
          className="peptalk-country-dropdown__search"
          placeholder="Search country"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          aria-label="Search countries"
          autoComplete="off"
        />
      </div>

      {/* Country list */}
      <ul
        ref={listRef}
        className="peptalk-country-dropdown__list"
        role="listbox"
        aria-label="Countries"
      >
        {filtered.length === 0 ? (
          <li className="peptalk-country-dropdown__empty">No results</li>
        ) : (
          filtered.map(country => (
            <li
              key={country.code}
              role="option"
              tabIndex={0}
              aria-selected={country.code === value}
              className={clsx('peptalk-country-dropdown__item', {
                'peptalk-country-dropdown__item--selected':
                  country.code === value,
              })}
              onClick={() => handleSelect(country)}
              onKeyDown={e => handleListKeyDown(e, country)}
            >
              <span className="peptalk-country-dropdown__item-flag">
                {country.flag}
              </span>
              <span className="peptalk-country-dropdown__item-code">
                {country.dialCode}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={clsx('peptalk-country-dropdown', className, {
        'peptalk-country-dropdown--open': isOpen,
        'peptalk-country-dropdown--disabled': disabled,
      })}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        className="peptalk-country-dropdown__trigger peptalk-form-element"
        onClick={openDropdown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Country code: ${selectedCountry?.name ?? ''} ${selectedCountry?.dialCode ?? ''}`}
      >
        <span className="peptalk-country-dropdown__flag">
          {selectedCountry?.flag}
        </span>
        <span className="peptalk-country-dropdown__dial-code">
          {selectedCountry?.dialCode}
        </span>
        <span className="peptalk-country-dropdown__chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {/* Panel rendered via React Portal into document.body.
          This escapes BOTH overflow:hidden on .peptalk-chat-window AND the
          CSS transform containing-block on .peptalk-chat-window
          (transform: translateY scale(0.95) traps position:fixed children).
          Portal container has --peptalk-* CSS vars copied from widget root
          so the panel retains full widget theming. */}
      {isOpen &&
        portalContainerRef.current &&
        ReactDOM.createPortal(panel, portalContainerRef.current)}
    </div>
  );
};

export const FilterableCountryDropdown: React.FC<
  FilterableCountryDropdownProps
> = (props: FilterableCountryDropdownProps) => {
  const Component = useCustomComponent({
    componentName: 'PhoneInputCountryDropdown',
    Component: FilterableCountryDropdownComponent,
  });

  return <Component {...props} />;
};
